import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
} from "recharts";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";

const num = (v) => Number(v || 0);
const fmt = (v) => num(v).toLocaleString();
const pct = (part, whole) => (num(whole) > 0 ? (num(part) / num(whole)) * 100 : 0);

// Each stage names the value it is a share OF, explicitly. Deriving it from
// "the row above" is what made Active installs divide by Push subscribers —
// zero until push ships, so it always read 0.0%.
const STAGES = [
  { key: "registrations", label: "Registrations", basis: null, accent: "#3B82F6" },
  { key: "installs", label: "PWA installs", basis: "registrations", accent: "#A855F7" },
  { key: "pushSubscribers", label: "Push subscribers", basis: "installs", accent: "#10B981" },
  { key: "activeInstalls", label: "Active installs (30d)", basis: "installs", accent: "#F59E0B" },
];

const StatCard = ({ label, value, sub, accent }) => (
  <Box
    sx={{
      position: "relative",
      overflow: "hidden",
      height: "100%",
      p: 2.25,
      borderRadius: "12px",
      border: "1px solid rgba(23,26,43,0.08)",
      background: "rgba(23,26,43,0.02)",
    }}
  >
    <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accent }} />
    <Typography sx={{ fontSize: 11, letterSpacing: 1, opacity: 0.6, textTransform: "uppercase", mb: 1 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
    {sub && <Typography sx={{ fontSize: 12, opacity: 0.5, mt: 0.75 }}>{sub}</Typography>}
  </Box>
);

const PwaStats = () => {
  const [funnel, setFunnel] = useState({});
  const [daily, setDaily] = useState([]);
  const [platforms, setPlatforms] = useState([]);

  useEffect(() => {
    // Each report is independent — a failing endpoint renders zeros rather than
    // taking the whole page down.
    const load = async (url, setter, fallback) => {
      try {
        const res = await getAllData(url);
        setter(res?.status === "success" && res?.data ? res.data : fallback);
      } catch (e) {
        setter(fallback);
      }
    };
    load(endPoints.api.PWA_REPORT_FUNNEL, setFunnel, {});
    load(`${endPoints.api.PWA_REPORT_DAILY}?days=30`, setDaily, []);
    load(endPoints.api.PWA_REPORT_PLATFORMS, setPlatforms, []);
  }, []);

  // Rows arrive as one per (day, event); pivot to one row per day for the chart.
  const chartData = useMemo(() => {
    const byDay = {};
    (daily || []).forEach((r) => {
      const day = String(r.day || "").slice(0, 10);
      if (!byDay[day]) byDay[day] = { day };
      byDay[day][r.event_name] = num(r.total);
    });
    return Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day));
  }, [daily]);

  const totalDevices = useMemo(
    () => (platforms || []).reduce((sum, p) => sum + num(p.devices), 0),
    [platforms]
  );

  const promptShown = num(funnel.promptShown);
  const installClicked = num(funnel.installClicked);

  return (
    <Box>
      <PageHeader
        title="PWA Stats"
        crumb="Monitor / PWA Stats"
        subtitle="Install funnel · registrations → installs → push subscribers → active"
      />

      <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
        {STAGES.map(({ key, label, basis, accent }) => (
          <Grid item xs={12} sm={6} md={3} key={key}>
            <StatCard
              label={label}
              accent={accent}
              value={fmt(funnel[key])}
              sub={
                basis
                  ? `${pct(funnel[key], funnel[basis]).toFixed(1)}% of ${
                      STAGES.find((s) => s.key === basis)?.label.toLowerCase()
                    }`
                  : null
              }
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
        <Grid item xs={12} md={5}>
          <SectionCard title="Install funnel" subtitle="Distinct authenticated users per stage">
            {STAGES.map(({ key, label, basis, accent }) => {
              const value = num(funnel[key]);
              const share = basis ? pct(value, funnel[basis]) : 100;
              return (
                <Box key={key} sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                    <Typography variant="body2">{label}</Typography>
                    <Typography variant="body2">
                      <strong>{fmt(value)}</strong>
                      {basis && <span style={{ opacity: 0.55, marginLeft: 8 }}>{share.toFixed(1)}%</span>}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 6, borderRadius: 3, background: "rgba(23,26,43,0.08)", overflow: "hidden" }}>
                    <Box
                      sx={{
                        width: `${Math.min(share, 100)}%`,
                        height: "100%",
                        borderRadius: 3,
                        background: accent,
                        transition: "width .4s ease",
                      }}
                    />
                  </Box>
                </Box>
              );
            })}

            <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid rgba(23,26,43,0.08)" }}>
              <Typography sx={{ fontSize: 11, letterSpacing: 1, opacity: 0.6, textTransform: "uppercase", mb: 1.5 }}>
                Prompt performance
              </Typography>
              <Grid container spacing={2}>
                {[
                  { label: "Prompt shown", value: fmt(promptShown) },
                  { label: "Install clicked", value: fmt(installClicked) },
                  { label: "Click-through", value: `${pct(installClicked, promptShown).toFixed(1)}%`, accent: true },
                  { label: "Installed devices", value: fmt(funnel.installedDevices) },
                ].map((m) => (
                  <Grid item xs={6} sm={3} key={m.label}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: m.accent ? "#10B981" : "inherit" }}>
                      {m.value}
                    </Typography>
                    <Typography sx={{ fontSize: 12, opacity: 0.55 }}>{m.label}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <SectionCard title="Events over time" subtitle="Last 30 days">
            {chartData.length ? (
              <Box sx={{ height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="pwa_install_prompt_shown" name="Prompt shown" stroke="#3B82F6" dot={false} />
                    <Line type="monotone" dataKey="pwa_install_clicked" name="Install clicked" stroke="#A855F7" dot={false} />
                    <Line type="monotone" dataKey="pwa_standalone_launch" name="Standalone launch" stroke="#10B981" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography sx={{ opacity: 0.55, py: 6 }}>No PWA events recorded yet.</Typography>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <SectionCard title="Installs by platform" subtitle="Devices that launched in standalone mode">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Platform</TableCell>
              <TableCell align="right">Devices</TableCell>
              <TableCell align="right">Users</TableCell>
              <TableCell align="right">Share</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(platforms || []).map((p) => (
              <TableRow key={p.platform}>
                <TableCell sx={{ textTransform: "capitalize" }}>{p.platform}</TableCell>
                <TableCell align="right">{fmt(p.devices)}</TableCell>
                <TableCell align="right">{fmt(p.users)}</TableCell>
                <TableCell align="right">{pct(p.devices, totalDevices).toFixed(1)}%</TableCell>
              </TableRow>
            ))}
            {!platforms?.length && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5, opacity: 0.55 }}>
                  No installs recorded yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </SectionCard>
    </Box>
  );
};

export default PwaStats;
